import { useEffect } from 'react';
import { useFormikContext } from 'formik';
import { useFormValues } from '../contexts/FormValuesContext';
import { FieldConfig } from '../services/ValidationService';
import { isPendingRawText, resolveDecimalFieldPolicy } from '../utils/decimalInput';
import { logDebug } from '../utils/utils';

/** The number fields holding text that is not a number yet, by the same rule as their input (slider or text). */
const draftPathsOf = (values: Record<string, unknown>, groupedFields: Record<string, FieldConfig[]>): Set<string> => {
    const draftPaths = new Set<string>();
    Object.entries(groupedFields).forEach(([group, fields]) => {
        const groupValues = values[group] as Record<string, unknown> | undefined;
        fields
            .filter(field => field.type === 'number' && isPendingRawText(groupValues?.[field.name], resolveDecimalFieldPolicy(field, 'dynamic')))
            .forEach(field => draftPaths.add(`${group}.${field.name}`));
    });
    return draftPaths;
};

const NO_FIELDS: Record<string, FieldConfig[]> = {};

/**
 * Hook to sync Formik values with FormValuesContext in real-time
 * Must be used inside a Formik component to access useFormikContext
 * Gracefully handles missing FormValuesProvider for test environments
 */
export const useFormikSyncToContext = (groupedFields: Record<string, FieldConfig[]> = NO_FIELDS) => {
    const formik = useFormikContext<Record<string, unknown>>();

    // Try to get FormValues context, but don't fail if it's not available (e.g., in tests)
    let updateFormValues: ((values: Record<string, unknown>, draftPaths?: ReadonlySet<string>) => void) | null = null;

    try {
        const formValuesContext = useFormValues();
        updateFormValues = formValuesContext.updateFormValues;
    } catch (error) {
        // FormValuesProvider not available (e.g., in test environment)
        logDebug('[useFormikSyncToContext]', 'FormValuesProvider not available, skipping sync');
    }

    useEffect(() => {
        if (updateFormValues) {
            // Update context whenever Formik values change
            updateFormValues(formik.values, draftPathsOf(formik.values, groupedFields));
            logDebug('[useFormikSyncToContext]', 'Syncing Formik values to context', formik.values);
        }
    }, [formik.values, updateFormValues, groupedFields]);

    return formik;
};
